import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import EditableDatable from "./EditableDatatable";
import ErrorMessage from "./ErrorPopup";
import SuccessMessage from "./SuccessPopup";
import {
  companyDefault,
  draftQuoteLines as DQL,
  items as IT,
} from "../HelperFx/datalinks";
import { parseApiError } from "../HelperFx/common";

const REACT_DATA_URL = process.env.REACT_APP_API_DATA_URL;

const matchesItem = (item, term) => {
  const query = String(term || "").toUpperCase();
  if (!query) return true;
  return [item.no, item.searchTerm].some((value) =>
    String(value || "").toUpperCase().includes(query),
  );
};

const DraftQuoteLines = ({
  docNo,
  token,
  allowEdit = true,
  onLinesChanged,
}) => {
  const [lineData, setLineData] = useState([]);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const fileInputRef = useRef(null);
  const linesApi = `${REACT_DATA_URL}${DQL}`;

  const showError = (message) => {
    setErrorMessage(message);
    setErrorOpen(true);
  };

  const mapLine = (line) => ({
    ...line,
    systemId: line.id || line.systemId,
    systemSerialId: line.id || line.systemId,
  });

  const applyServerRow = (updated, rowId, serverRow) => {
    const mapped = mapLine(serverRow);
    const idx = updated.findIndex((row) => row.systemId === rowId);
    if (idx === -1) return;
    updated[idx] = {
      ...updated[idx],
      ...mapped,
      systemId: rowId,
      systemSerialId: mapped.systemId,
    };
  };

  const loadLines = useCallback(async () => {
    if (!docNo) return [];
    const response = await axios.get(
      `${linesApi}?${companyDefault}&$filter=documentNumber eq '${docNo}'`,
      { headers: { Authorization: `Basic ${token}` } },
    );
    const rows = (response.data?.value || []).map(mapLine);
    setLineData(rows);
    return rows;
  }, [docNo, linesApi, token]);

  useEffect(() => {
    loadLines().catch((err) => showError(parseApiError(err)));
  }, [loadLines]);

  const searchItems = useCallback(
    async (searchTerm) => {
      const term = String(searchTerm || "").trim();
      if (!term) return [];

      const safe = term.toUpperCase().replace(/'/g, "''");
      const headers = { Authorization: `Basic ${token}` };
      const collected = [];
      const seen = new Set();

      const addItems = (items) => {
        (items || []).forEach((item) => {
          if (!item?.no || seen.has(item.no)) return;
          seen.add(item.no);
          collected.push(item);
        });
      };

      const tryFilter = async (filter) => {
        try {
          const response = await axios.get(
            `${REACT_DATA_URL}${IT}?${companyDefault}&$filter=${encodeURIComponent(filter)}&$top=100`,
            { headers },
          );
          return response.data?.value || [];
        } catch {
          return null;
        }
      };

      const combined = await tryFilter(
        `contains(no,'${safe}') or contains(searchTerm,'${safe}')`,
      );
      if (combined) {
        addItems(combined);
      } else {
        addItems((await tryFilter(`contains(no,'${safe}')`)) || []);
        if (collected.length < 50) {
          addItems((await tryFilter(`contains(searchTerm,'${safe}')`)) || []);
        }
      }

      return collected.slice(0, 100).map((item) => ({
        value: item.no,
        label: `${item.no} - ${item.description || ""}`,
        description: item.description,
        baseUOM: item.baseUOM,
        no: item.no,
      }));
    },
    [token],
  );

  const lookupItem = async (itemNo) => {
    const safe = String(itemNo || "").trim().toUpperCase().replace(/'/g, "''");
    const response = await axios.get(
      `${REACT_DATA_URL}${IT}?${companyDefault}&$filter=no eq '${safe}'`,
      { headers: { Authorization: `Basic ${token}` } },
    );
    return response.data?.value?.[0] || null;
  };

  const handlePageBlur = async (id, field, value, updated) => {
    const row = updated.find((r) => r.systemId === id);
    if (!row) return;

    const isNew =
      String(row.systemId || "").startsWith("empty") && !row.systemSerialId;

    try {
      if (isNew) {
        const selected = value && typeof value === "object" ? value : row.selectedItem;
        const itemNo = selected?.value || selected?.no || row.itemNo;
        if (!itemNo) return;

        const payload = {
          lineType: "Item",
          documentNumber: docNo,
          documentType: "Quote",
          itemNo,
          description: selected?.description || row.description || "",
          description2: row.description2 || "",
          uom: selected?.baseUOM || row.uom || "",
          quantity: Number(row.quantity) > 0 ? Number(row.quantity) : 1,
        };

        const response = await axios.post(`${linesApi}?${companyDefault}`, payload, {
          headers: {
            Authorization: `Basic ${token}`,
            "Content-Type": "application/json",
          },
        });
        applyServerRow(updated, id, response.data);
        if (typeof onLinesChanged === "function") onLinesChanged();
        return response.data;
      }

      const systemId = row.systemSerialId || row.id || row.systemId;
      const patchPayload =
        field === "itemNo" && value && typeof value === "object"
          ? {
              itemNo: value.value,
              description: value.description || "",
              uom: value.baseUOM || "",
            }
          : field === "quantity"
            ? { quantity: Number(value) || 0 }
            : { [field]: value };

      const response = await axios.patch(
        `${linesApi}(${systemId})?${companyDefault}`,
        patchPayload,
        {
          headers: {
            Authorization: `Basic ${token}`,
            "Content-Type": "application/json",
            "If-Match": "*",
          },
        },
      );
      applyServerRow(updated, id, response.data);
      if (typeof onLinesChanged === "function") onLinesChanged();
    } catch (error) {
      throw new Error(parseApiError(error));
    }
  };

  const exportTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { "Item No": "ITM001", "Customer Description": "Sample Description", Quantity: 1 },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Quotation_Lines_Template.xlsx");
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = null;
    if (!file) return;

    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const workbook = XLSX.read(data, { type: "array" });
      const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]);
      const errors = [];
      let successCount = 0;

      for (const [index, row] of json.entries()) {
        const itemNo = row["Item No"]?.toString().trim();
        const qty = Number(row["Quantity"] || 0);
        const desc2 = row["Customer Description"] || "";

        if (!itemNo) {
          errors.push(`Row ${index + 1}: Item No is required.`);
          continue;
        }

        try {
          const itemDetails = await lookupItem(itemNo);
          if (!itemDetails) {
            errors.push(`Row ${index + 1}: Item "${itemNo}" was not found.`);
            continue;
          }

          await axios.post(
            `${linesApi}?${companyDefault}`,
            {
              lineType: "Item",
              documentNumber: docNo,
              documentType: "Quote",
              itemNo: itemDetails.no,
              description: itemDetails.description,
              description2: desc2,
              uom: itemDetails.baseUOM,
              quantity: qty || 1,
            },
            {
              headers: {
                Authorization: `Basic ${token}`,
                "Content-Type": "application/json",
              },
            },
          );
          successCount += 1;
        } catch (err) {
          errors.push(`Row ${index + 1}: ${parseApiError(err)}`);
        }
      }

      await loadLines();

      if (errors.length) {
        showError(
          `Added ${successCount} line(s).\n\n${errors.join("\n")}`,
        );
      } else {
        setSuccessMessage(`Successfully added ${successCount} line(s).`);
        setSuccessOpen(true);
      }
    } catch (err) {
      showError(parseApiError(err));
    }
  };

  const columns = useMemo(
    () => [
    {
      label: "Item No.",
      type: "popupSelect",
      name: "itemNo",
      selectOnly: true,
      minChars: 1,
      placeholder: "Click to search item",
      fetchOptions: searchItems,
      sortable: true,
      width: "220px",
      disabled: !allowEdit,
    },
    {
      label: "Description",
      type: "text",
      name: "description",
      sortable: true,
      width: "220px",
      disabled: true,
    },
    {
      label: "Customer Description",
      type: "text",
      name: "description2",
      sortable: true,
      width: "200px",
      disabled: !allowEdit,
    },
    {
      label: "Quantity",
      type: "decimal",
      name: "quantity",
      sortable: true,
      width: "110px",
      disabled: !allowEdit,
    },
    {
      label: "UOM",
      type: "text",
      name: "uom",
      sortable: true,
      width: "90px",
      disabled: true,
    },
    {
      label: "Unit Price",
      type: "number",
      name: "unitPrice",
      format: "amount",
      sortable: true,
      width: "110px",
      disabled: true,
    },
    {
      label: "Line Amount",
      type: "number",
      name: "lineAmount",
      format: "amount",
      sortable: true,
      width: "120px",
      disabled: true,
    },
    ],
    [allowEdit, searchItems],
  );

  const actions = allowEdit
    ? [
        { name: "Excel Template", fx: exportTemplate },
        { name: "Import Excel", fx: () => fileInputRef.current?.click() },
      ]
    : [];

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        hidden
        onChange={handleExcelUpload}
      />
      <EditableDatable
        columns={columns}
        initialData={lineData}
        pageOnBlur={handlePageBlur}
        universalDisabled={!allowEdit}
        linesApi={linesApi}
        converteddocType="Quote"
        AllowDelete={allowEdit}
        onDataRefresh={loadLines}
        LineDescription="Request Quotation Lines"
        SecondaryDescription="Click Item No. or Customer Description to edit. Search and pick an item to add a line."
        actions={actions}
        authToken={token}
        useLinesApiForDeleteAll
      />
      <ErrorMessage
        isOpen={errorOpen}
        toggle={() => setErrorOpen(false)}
        caption="Error"
        message={errorMessage}
      />
      <SuccessMessage
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        caption="Success"
        message={successMessage}
      />
    </>
  );
};

export default DraftQuoteLines;
